import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { adminAPI } from '../services/api';
import { hasPermission } from '../utils/auth';
import { t } from '../utils/i18n';

function AdminPanel() {
    const [stats, setStats] = useState({});
    const [users, setUsers] = useState([]);
    const [activityLogs, setActivityLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, forceUpdate] = useState(0);
    const [activeTab, setActiveTab] = useState('stats');
    const [showUserModal, setShowUserModal] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [newUser, setNewUser] = useState({
        username: '',
        password: '',
        full_name: '',
        email: '',
        role: 'translator'
    });
    const [permissions, setPermissions] = useState({});
    const [selectedRole, setSelectedRole] = useState('facilitator');

    useEffect(() => {
        if (!hasPermission('canManageUsers')) {
            window.location.href = '/dashboard';
            return;
        }
        loadData();
    }, []);

    useEffect(() => {
        const handleLanguageChange = () => {
            forceUpdate(prev => prev + 1);
        };
        window.addEventListener('languageChanged', handleLanguageChange);
        return () => window.removeEventListener('languageChanged', handleLanguageChange);
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [statsRes, usersRes, activityRes, permissionsRes] = await Promise.all([
                adminAPI.getStats(),
                adminAPI.getUsers(),
                adminAPI.getActivityLogs(50, 0),
                adminAPI.getPermissions()
            ]);

            if (statsRes.data.success) setStats(statsRes.data.stats);
            if (usersRes.data.success) setUsers(usersRes.data.users);
            if (activityRes.data.success) setActivityLogs(activityRes.data.logs);
            if (permissionsRes.data.success) setPermissions(permissionsRes.data.permissions);
        } catch (error) {
            console.error('Error loading admin data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePermissionChange = async (role, permission, value) => {
        try {
            const updatedPermissions = {
                ...permissions,
                [role]: {
                    ...permissions[role],
                    [permission]: value
                }
            };
            
            setPermissions(updatedPermissions);
            
            await adminAPI.updatePermissions(role, updatedPermissions[role]);
            alert(t('saved'));
        } catch (error) {
            console.error('Error updating permission:', error);
            alert(t('error'));
            // Revert on error
            loadData();
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            const response = await adminAPI.createUser(newUser);
            if (response.data.success) {
                setShowUserModal(false);
                setNewUser({ username: '', password: '', full_name: '', email: '', role: 'translator' });
                loadData();
                alert(t('userCreatedSuccess'));
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error || 
                                error.response?.data?.errors?.map(e => e.msg).join(', ') ||
                                'Error creating user';
            alert(errorMessage);
            console.error('Create user error:', error.response?.data);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <div className="loading" style={{ width: '40px', height: '40px', margin: '0 auto' }}></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="content-header">
                <h1 className="page-title">{t('admin')}</h1>
                <p className="page-description">{t('manageSystem')}</p>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', borderBottom: '1px solid var(--gray-200)' }}>
                <button
                    className={`btn ${activeTab === 'stats' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setActiveTab('stats')}
                >
                    <i className="fas fa-chart-bar"></i>
                    <span>{t('statistics')}</span>
                </button>
                <button
                    className={`btn ${activeTab === 'users' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setActiveTab('users')}
                >
                    <i className="fas fa-users"></i>
                    <span>{t('users')}</span>
                </button>
                <button
                    className={`btn ${activeTab === 'activity' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setActiveTab('activity')}
                >
                    <i className="fas fa-history"></i>
                    <span>{t('activities')}</span>
                </button>
                <button
                    className={`btn ${activeTab === 'permissions' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setActiveTab('permissions')}
                >
                    <i className="fas fa-key"></i>
                    <span>{t('permissions')}</span>
                </button>
            </div>

            {activeTab === 'stats' && (
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon">
                            <i className="fas fa-project-diagram"></i>
                        </div>
                        <div className="stat-value">{stats.totalProjects || 0}</div>
                        <div className="stat-label">{t('totalProjects')}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon secondary">
                            <i className="fas fa-language"></i>
                        </div>
                        <div className="stat-value">{stats.totalEntries || 0}</div>
                        <div className="stat-label">{t('translations')}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon accent">
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <div className="stat-value">{stats.confirmedEntries || 0}</div>
                        <div className="stat-label">{t('confirmed')}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: 'rgba(96, 173, 155, 0.1)', color: '#60AD9B' }}>
                            <i className="fas fa-users"></i>
                        </div>
                        <div className="stat-value">{stats.activeUsers || 0}</div>
                        <div className="stat-label">{t('activeUsers')}</div>
                    </div>
                </div>
            )}

            {activeTab === 'users' && (
                <div className="table-container">
                    <div className="table-header">
                        <h2 className="table-title">{t('users')}</h2>
                        <button
                            onClick={() => setShowUserModal(true)}
                            className="btn btn-primary"
                        >
                            <i className="fas fa-plus"></i>
                            <span>{t('newUser')}</span>
                        </button>
                    </div>
                    <div className="table-responsive">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>{t('username')}</th>
                                    <th>{t('name')}</th>
                                    <th>{t('email')}</th>
                                    <th>{t('role')}</th>
                                    <th>{t('projects')}</th>
                                    <th>{t('status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user) => (
                                    <tr key={user.id}>
                                        <td>{user.username}</td>
                                        <td>{user.full_name}</td>
                                        <td>{user.email || '-'}</td>
                                        <td>
                                            <span className="status-badge active">{t(user.role)}</span>
                                        </td>
                                        <td>{user.project_count}</td>
                                        <td>
                                            <span className={`status-badge ${user.is_active ? 'confirmed' : 'archived'}`}>
                                                {user.is_active ? t('active') : t('inactive')}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'activity' && (
                <div className="table-container">
                    <div className="table-header">
                        <h2 className="table-title">{t('activityHistory')}</h2>
                    </div>
                    <div className="table-responsive">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>{t('user')}</th>
                                    <th>{t('action')}</th>
                                    <th>{t('details')}</th>
                                    <th>{t('date')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activityLogs.map((log) => (
                                    <tr key={log.id}>
                                        <td>{log.full_name || log.username || 'System'}</td>
                                        <td>{log.action}</td>
                                        <td>{log.details || '-'}</td>
                                        <td>{new Date(log.created_at).toLocaleString('hy-AM')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {showUserModal && (
                <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">{t('createUser')}</h3>
                        </div>
                        <form onSubmit={handleCreateUser}>
                            <div className="form-group">
                                <label className="form-label">{t('username')}</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={newUser.username}
                                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{t('password')}</label>
                                <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className="form-input"
                                        value={newUser.password}
                                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                        required
                                        minLength={6}
                                        style={{ paddingRight: '50px', width: '100%', flex: 1 }}
                                    />
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setShowPassword(!showPassword);
                                        }}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        style={{
                                            position: 'absolute',
                                            right: '15px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '10px',
                                            color: '#666',
                                            fontSize: '20px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            zIndex: 100,
                                            outline: 'none',
                                            minWidth: '30px',
                                            minHeight: '30px'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.color = '#333';
                                            e.currentTarget.style.background = '#f0f0f0';
                                            e.currentTarget.style.borderRadius = '4px';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.color = '#666';
                                            e.currentTarget.style.background = 'transparent';
                                        }}
                                        title={showPassword ? 'Թաքցնել գաղտնաբառը' : 'Ցուցադրել գաղտնաբառը'}
                                    >
                                        <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'} style={{ pointerEvents: 'none' }}></i>
                                    </button>
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-label">{t('name')}</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={newUser.full_name}
                                    onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{t('email')}</label>
                                <input
                                    type="email"
                                    className="form-input"
                                    value={newUser.email}
                                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{t('role')}</label>
                                <select
                                    className="form-select"
                                    value={newUser.role}
                                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                                >
                                    <option value="translator">{t('translator')}</option>
                                    <option value="editor">{t('editor')}</option>
                                    <option value="reviewer">{t('reviewer')}</option>
                                    <option value="confirmer">{t('confirmer')}</option>
                                    <option value="facilitator">{t('facilitator')}</option>
                                    <option value="admin">{t('admin')}</option>
                                </select>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-outline"
                                    onClick={() => setShowUserModal(false)}
                                >
                                    {t('cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    <i className="fas fa-plus"></i>
                                    <span>{t('create')}</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
}

export default AdminPanel;

